import React, { useEffect, useRef, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import HtmlParser from 'react-html-parser';
import Swal from 'sweetalert2';

import Menu from '../main/Menu';
import useAuth from '../../auth/useAuth';
import {
    settings, styles, setDateTimeFormatGlobal, controlMethods, validateEmail, validateTelegramId, isUndNullEmpNew, isUndNullNew, sendMessageCloseCurrentWindow,
    getError, getResponse, LoadingSection, getLanguageObj, awaitDelay
} from '../../helpers/UtilsHelper';
import { AccountService } from '../../services/accounts/AccountService';
import { GlobalFormats } from '../../classes/main/globalFormatClass';
import { SwalPropertiesClass } from '../../classes/main/swalPropertiesClass';

const currentMethod = 'AccountsDetails - function';

const { app, colors, html, icons, images, messages, labels, react } = settings;
const { urls } = app?.backend;

export default function AccountDetails({
    languageObj = GlobalFormats?.getClass(getLanguageObj({ subMethod: currentMethod })?.result),
} = {}) {
    //#region variables
    const auth = useAuth();
    const { id } = useParams();

    const [user, setUser] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    // Consolidated process state
    const [process, setProcess] = useState({
        showLoading: false,
        save: false,
        authorizate: false,
        getToken: false,
        clearTokenLogs: false,
        getUserInfo: false,
        back: false
    });

    // Consolidated validation errors state
    const [errors, setErrors] = useState({
        userName: '',
        code: '',
        password: '',
        notificationsEmail: '',
        notificationsTelegram: ''
    });

    const isMount = useRef(true);
    const dateTimeFormat = {
        date: languageObj?.format?.date?.f1,
        time: languageObj?.format?.time?.type,
    };
    const userNotifications = { email: {}, telegram: { maxLength: 10, regExp1: /[^0-9.-]/g, regExp2: /(\..*)\./g } };
    const icons_ = {
        envelope: 'fas fa-lg fa-envelope',
        idBadge: 'fas fa-lg fa-id-badge',
        building: 'fas fa-lg fa-building',
        phone: 'fas fa-lg fa-phone',
        calendarAlt: 'fas fa-lg fa-calendar-alt',
    };
    //#endregion variables

    //#region methods
    const setDefaultValues = ({
        subMethod = ''
    } = {}) => {
        const currentMethod = 'setDefaultValues';
        let response = getResponse({ type: 0 });
        try {
            setErrors({
                userName: '',
                code: '',
                password: '',
                notificationsEmail: '',
                notificationsTelegram: ''
            });
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
        return response;
    };

    const formDataValidate = ({
        subMethod = ''
    } = {}) => {
        const currentMethod = 'formDataValidate';
        let response = getResponse({ type: 0 });
        try {
            const thisFieldRequired = messages?.thisFieldRequired?.[languageObj?.id];
            const errors_ = {
                userName: '',
                code: '',
                password: '',
                notificationsEmail: '',
                notificationsTelegram: ''
            };

            if (isUndNullEmpNew({ data: user?.userName, subMethod: currentMethod })?.result) {
                errors_.userName = thisFieldRequired;
                response = getResponse({ type: 1 });
            }

            if (!isUndNullEmpNew({ data: user?.notifications?.email, subMethod: currentMethod })?.result &&
                !validateEmail(user?.notifications?.email)) {
                errors_.notificationsEmail = messages?.youMustEnterValidEmail?.[languageObj?.id];
                response = getResponse({ type: 1 });
            }

            if (!isUndNullNew({ data: user?.notifications?.telegram, subMethod: currentMethod })?.result) {
                const responseTwo = validateTelegramId({ id: user?.notifications?.telegram, subMethod: currentMethod });
                if (!responseTwo?.isSuccess || responseTwo?.isWarning) {
                    response = responseTwo;
                } else {
                    if (!responseTwo?.result) {
                        errors_.notificationsTelegram = messages?.youMustEnterValidTelegramId?.[languageObj?.id];
                        response = getResponse({ type: 1 });
                    }
                }
            }

            setErrors(errors_);
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
        return response;
    };

    const getData = async ({
        subMethod = ''
    } = {}) => {
        const currentMethod = 'getData';
        let response = getResponse({ type: 0 });
        try {
            setProcess(prev => ({ ...prev, showLoading: true }));

            // Decoupled API call to AccountService
            const token = auth?.account?.tokenVzLaML?.token;
            response = await AccountService.get({ id, token, subMethod: currentMethod });

            if (!response?.isSuccess || response?.isWarning) return;

            const userLocal = response?.result || {};
            setUser(userLocal);
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false }));

            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    };

    const updateUser = async ({
        e = undefined,
        subMethod = ''
    } = {}) => {
        const currentMethod = 'updateUser';
        let response = getResponse({ type: 0 });
        try {
            e?.preventDefault();
            setShowPassword(false);

            // Reset validation errors
            response = setDefaultValues({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            // Form validation
            response = formDataValidate({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            setProcess(prev => ({ ...prev, showLoading: true, save: true }));

            // Decoupled API call to AccountService
            const token = auth?.account?.tokenVzLaML?.token;
            response = await AccountService.update({
                id: user?.id,
                account: user,
                token,
                subMethod: currentMethod
            });

            if (!response?.isSuccess || response?.isWarning) return;

            const userLocal = response?.result || {};
            setUser(userLocal);

            response.msgText = `${messages?.processCompletedSuccessfully?.[languageObj?.id]}.`;
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false, save: false }));

            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    };

    const getUserInfo = async ({
        e = undefined,
        subMethod = ''
    } = {}) => {
        const currentMethod = 'getUserInfo';
        let response = getResponse({ type: 0 });
        try {
            e?.preventDefault();
            setShowPassword(false);

            // Reset validation errors
            response = setDefaultValues({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            // Check if code exists before proceeding
            if (isUndNullEmpNew({ data: user?.code, subMethod: currentMethod })?.result) {
                setErrors(prev => ({ ...prev, code: messages?.thisFieldRequired?.[languageObj?.id] }));
                response = getResponse({ type: 1 });
                response.msgText = messages?.userMustHaveCodeBeforeGeneratingToken?.[languageObj?.id];
                return;
            }

            // Swal Confirmation dialog
            let swalProperties = SwalPropertiesClass?.getClass();
            swalProperties = {
                icon: 'warning',
                title: labels?.warning?.[languageObj?.id],
                html: messages?.doYouWantUpdateData?.[languageObj?.id],
                timer: null,
                confirmButtonColor: colors?.warningColorExp,
                confirmButtonText: labels?.yes?.[languageObj?.id],
                showCancelButton: true,
                cancelButtonColor: colors?.successColorExp,
                cancelButtonText: labels?.no?.[languageObj?.id],
                customClass: {
                    popup: html?.popup,
                    confirmButton: html?.buttons?.primary,
                    cancelButton: html?.buttons?.secondary,
                },
                didOpen: () => {
                    Swal.hideLoading();
                }
            };

            const { isConfirmed } = await Swal.fire({ ...swalProperties });
            if (!isConfirmed) return;

            setProcess(prev => ({ ...prev, showLoading: true, getUserInfo: true }));

            // Decoupled API call to AccountService
            const token = auth?.account?.tokenVzLaML?.token;
            response = await AccountService.getInformation({
                id: user?.id,
                token,
                subMethod: currentMethod
            });

            if (!response?.isSuccess || response?.isWarning) return;

            const userLocal = response?.result?.user || {};
            setUser(userLocal);

            response.msgText = `${messages?.processCompletedSuccessfully?.[languageObj?.id]}.`;
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false, getUserInfo: false }));

            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    };

    const getUserAuthorize = async ({
        e = undefined,
        subMethod = ''
    } = {}) => {
        const currentMethod = 'getUserAuthorize';
        let response = getResponse({ type: 0 });
        try {
            e?.preventDefault();

            let swalProperties = SwalPropertiesClass?.getClass();
            swalProperties = {
                icon: 'warning',
                title: labels?.warning?.[languageObj?.id],
                html: messages?.doYouWantGenerateAuthorization?.[languageObj?.id],
                timer: null,
                confirmButtonColor: colors?.warningColorExp,
                confirmButtonText: labels?.yes?.[languageObj?.id],
                showCancelButton: true,
                cancelButtonColor: colors?.successColorExp,
                cancelButtonText: labels?.no?.[languageObj?.id],
                customClass: {
                    popup: html?.popup,
                    confirmButton: html?.buttons?.primary,
                    cancelButton: html?.buttons?.secondary,
                },
                didOpen: () => {
                    Swal.hideLoading();
                }
            };

            const { isConfirmed } = await Swal.fire({ ...swalProperties });
            if (isConfirmed) {
                window.open(`${settings?.mercadoLibre?.urlAuthorization}${user?.appId}`, '_blank', 'noopener,noreferrer');
            }
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    };

    const getUserToken = async ({
        e = undefined,
        subMethod = ''
    } = {}) => {
        const currentMethod = 'getUserToken';
        let response = getResponse({ type: 0 });
        try {
            e?.preventDefault();
            setShowPassword(false);

            // Reset validation errors
            response = setDefaultValues({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            // Check if code exists before token generation
            if (isUndNullEmpNew({ data: user?.code, subMethod: currentMethod })?.result) {
                setErrors(prev => ({ ...prev, code: messages?.thisFieldRequired?.[languageObj?.id] }));
                response = getResponse({ type: 1 });
                response.msgText = messages?.userMustHaveCodeBeforeGeneratingToken?.[languageObj?.id];
                return;
            }

            const swalProperties = {
                icon: 'warning',
                title: labels?.warning?.[languageObj?.id],
                html: messages?.doYouWantToGenerateTheToken?.[languageObj?.id],
                timer: null,
                confirmButtonColor: colors?.warningColorExp,
                confirmButtonText: labels?.yes?.[languageObj?.id],
                showCancelButton: true,
                cancelButtonColor: colors?.successColorExp,
                cancelButtonText: labels?.no?.[languageObj?.id],
                customClass: {
                    popup: html?.popup,
                    confirmButton: html?.buttons?.primary,
                    cancelButton: html?.buttons?.secondary,
                },
                didOpen: () => {
                    Swal.hideLoading();
                }
            };

            const { isConfirmed } = await Swal.fire({ ...swalProperties });
            if (!isConfirmed) return;

            setProcess(prev => ({ ...prev, showLoading: true, getToken: true }));

            // Decoupled API call to AccountService
            const token = auth?.account?.tokenVzLaML?.token;
            response = await AccountService.generateToken({
                id: user?.id,
                token,
                subMethod: currentMethod
            });

            if (!response?.isSuccess || response?.isWarning) return;

            const userLocal = response?.result?.user || {};
            setUser(userLocal);

            response.msgText = `${messages?.processCompletedSuccessfully?.[languageObj?.id]}.`;
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false, getToken: false }));

            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    };

    const clearUserTokenLogs = async ({
        e = undefined,
        subMethod = ''
    } = {}) => {
        const currentMethod = 'clearUserTokenLogs';
        let response = getResponse({ type: 0 });
        try {
            e?.preventDefault();
            setShowPassword(false);

            const swalProperties = {
                icon: 'warning',
                title: labels?.warning?.[languageObj?.id],
                html: messages?.thisProcessIrreversibleDoYouWishContinue?.[languageObj?.id],
                timer: null,
                confirmButtonColor: colors?.warningColorExp,
                confirmButtonText: labels?.yes?.[languageObj?.id],
                showCancelButton: true,
                cancelButtonColor: colors?.successColorExp,
                cancelButtonText: labels?.no?.[languageObj?.id],
                customClass: {
                    popup: html?.popup,
                    confirmButton: html?.buttons?.primary,
                    cancelButton: html?.buttons?.secondary,
                },
                didOpen: () => {
                    Swal.hideLoading();
                }
            };

            const { isConfirmed } = await Swal.fire(swalProperties);
            if (!isConfirmed) return;

            setProcess(prev => ({ ...prev, showLoading: true, clearTokenLogs: true }));

            // Decoupled API call to AccountService
            const token = auth?.account?.tokenVzLaML?.token;
            response = await AccountService.clearTokenLogs({
                id: user?.id,
                token,
                subMethod: currentMethod
            });

            if (!response?.isSuccess || response?.isWarning) return;

            const userLocal = response?.result?.user || {};
            setUser(userLocal);

            response.msgText = `${messages?.processCompletedSuccessfully?.[languageObj?.id]}.`;
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false, clearTokenLogs: false }));

            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    };

    const setFormValue = ({
        key = '',
        value = ''
    } = {}) => {
        setUser(prev => {
            if (key === 'notificationsEmail') {
                return {
                    ...prev,
                    notifications: {
                        ...prev?.notifications,
                        email: value
                    }
                };
            }
            if (key === 'notificationsTelegram') {
                return {
                    ...prev,
                    notifications: {
                        ...prev?.notifications,
                        telegram: value
                    }
                };
            }
            return { ...prev, [key]: value };
        });
    };

    const handleInputFocus = ({ key }) => {
        setErrors(prev => ({ ...prev, [key]: '' }));
    };

    const gotoBack = async ({
        subMethod = ''
    }) => {
        const currentMethod = 'gotoBack';
        let response = getResponse({ type: 0 });
        try {
            setProcess(prev => ({ ...prev, showLoading: true, back: true }));
            await awaitDelay({ delayTime: settings?.timers?.oneSeconds });
            window.location.href = urls?.accounts;
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false, back: false }));

            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    }

    useEffect(() => {
        isMount.current = true;
        getData({ subMethod: 'onLoad - form' });
        return () => { isMount.current = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    //#endregion methods

    return (
        <div>
            <Menu viewName={'accounts'} process={'accounts'} />

            <div className='content-wrapper bg-transparent'>
                {/* Content Header (Page header) */}
                <div className='content-header'>
                    <div className='container-fluid'>
                        <div className='row mb-2 align-items-center bg-white p-3 rounded shadow-lg mx-0'>
                            <div className='col-sm-6'>
                                <h4 className='m-0 font-weight-bold text-dark'>{labels?.account?.[languageObj?.id]} ({labels?.details?.[languageObj?.id]})</h4>
                            </div>
                            <div className='col-sm-6'>
                                <ol className='breadcrumb float-sm-right bg-transparent mb-0'>
                                    <li className='breadcrumb-item'><NavLink to={urls?.home}>{labels?.home?.[languageObj?.id]}</NavLink></li>
                                    <li className='breadcrumb-item'><NavLink to={urls?.accounts}>{labels?.accounts?.[languageObj?.id]}</NavLink></li>
                                    <li className='breadcrumb-item active text-primary'>{labels?.details?.[languageObj?.id]}</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main content */}
                <section className='content'>
                    <div className='container-fluid'>
                        <div className='card border-0 shadow-lg login-card' style={{ borderRadius: '8px' }}>
                            <LoadingSection alt='loadingSection'
                                className='overlay dark text-white'
                                height={20}
                                hidden={!process?.showLoading}
                                width={20}
                                languageId={languageObj?.id}
                                subMethod={currentMethod} />

                            <div className='card-header bg-white border-bottom-0 pt-4 px-4'>
                                <div className='d-flex align-items-center'>
                                    <div className='bg-light p-2 rounded-circle mr-3 shadow-sm'
                                        style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img src={images?.user}
                                            alt='userLogo'
                                            style={styles.cardHeaderLogo} />
                                    </div>
                                    <div className='d-flex flex-column'>
                                        <h5 className='mb-0 font-weight-bold text-dark'>{labels?.edit?.[languageObj?.id]}</h5>
                                        <div className='mt-1'>
                                            <span className='badge badge-pill badge-info px-2 py-1 text-uppercase' style={html?.badges?.micro}>
                                                <i className={`${icons?.fasFa?.idCard || 'fas fa-id-card'} mr-1`}></i> {labels?.getUserInfo?.[languageObj?.id]}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={(e) => updateUser({ e, subMethod: 'onSubmit - form' })}>
                                <div className='card-body px-4 pt-2'>
                                    <div className='row'>
                                        {/* Details Panel - Left Column */}
                                        <div className='col-12 col-md-7 mb-4 mb-md-0'>
                                            <div className='card border-0 shadow-sm bg-light h-100'
                                                style={{ borderRadius: '12px', borderLeft: `5px solid ${colors?.primaryColorExp || '#6366f1'}` }}>
                                                <div className='card-header bg-transparent border-bottom-0 pt-4 px-4'>
                                                    <span className='h6 font-weight-bold text-dark mb-0'>
                                                        <i className={`${icons?.fasFa?.idCard || 'fas fa-id-card'} mr-2 text-primary`} /> Mercado Libre Profile
                                                    </span>
                                                </div>
                                                <div className='card-body pt-0 px-4'>
                                                    <div className='row align-items-center'>
                                                        <div className='col-12 col-sm-8 order-2 order-sm-1'>
                                                            <h2 id='iGetFullName' className='lead font-weight-bold text-dark mb-3'>
                                                                {user?.firstName || '---'}, {user?.lastName || '---'}
                                                            </h2>
                                                            <div className='text-muted text-sm'>
                                                                <ul className='ml-0 mb-0 fa-ul text-muted' style={{ listStyleType: 'none', paddingLeft: 0 }}>
                                                                    <li id='liEmail' className='mb-2 d-flex align-items-center'>
                                                                        <span className='mr-2 text-primary'><i className={icons_?.envelope}></i></span>
                                                                        <span>{labels?.email?.[languageObj?.id]}: <strong className='text-dark'>{user?.email || '---'}</strong></span>
                                                                    </li>
                                                                    <li id='liSecondUserId' className='mb-2 d-flex align-items-center'>
                                                                        <span className='mr-2 text-primary'><i className={icons_?.idBadge}></i></span>
                                                                        <span>{labels?.userId?.[languageObj?.id]}: <strong className='text-dark'>{user?.secondId || '---'}</strong></span>
                                                                    </li>
                                                                    <li id='liAddress' className='mb-2 d-flex align-items-center'>
                                                                        <span className='mr-2 text-primary'><i className={icons_?.building}></i></span>
                                                                        <span>{labels?.address?.[languageObj?.id]}: <strong className='text-dark'>{user?.address || '---'}</strong></span>
                                                                    </li>
                                                                    <li id='liPhone' className='mb-2 d-flex align-items-center'>
                                                                        <span className='mr-2 text-primary'><i className={icons_?.phone}></i></span>
                                                                        <span>{labels?.phones?.[languageObj?.id]}: <strong className='text-dark'>{user?.phone || '---'}</strong></span>
                                                                    </li>
                                                                    {!isUndNullNew({ data: user?.createAtFm, subMethod: currentMethod })?.result && (
                                                                        <li id='liRegisterDate' className='d-flex align-items-center'>
                                                                            <span className='mr-2 text-primary'><i className={icons_?.calendarAlt}></i></span>
                                                                            <span>{labels?.registerDate?.[languageObj?.id]}: <strong className='text-dark'>{setDateTimeFormatGlobal({ date: user?.createAtFm, type: dateTimeFormat?.date, format: dateTimeFormat?.time, addTime: false, withSeconds: false, subMethod: currentMethod })?.result}</strong></span>
                                                                        </li>
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                        <div className='col-12 col-sm-4 text-center order-1 order-sm-2 mb-3 mb-sm-0'>
                                                            <img id='imgGetFmUserImageFullPath'
                                                                className='profile-user-img img-circle img-fluid shadow-sm'
                                                                src={!isUndNullEmpNew({ data: user?.pictureUrl, subMethod: currentMethod })?.result ? user?.pictureUrl : images?.noImage}
                                                                alt='FmUser'
                                                                style={{ ...styles_.pictureUser, borderRadius: '50%', border: '3px solid #EEF2FF' }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Credentials & Form Inputs - Right Column */}
                                        <div className='col-12 col-md-5'>
                                            {/* Username */}
                                            <div className='form-group mb-4'>
                                                <label className='small font-weight-bold text-uppercase form-label-uppercase'>
                                                    {labels?.user?.[languageObj?.id]}
                                                </label>
                                                <div className='position-relative'>
                                                    <i className={`${icons?.fasFa?.addressCard || 'fas fa-address-card'} position-absolute input-icon-left`} />
                                                    <input className='form-control border-0 shadow-sm form-input-icon'
                                                        onChange={(e) => setFormValue({ key: 'userName', value: e.target.value })}
                                                        onFocus={() => handleInputFocus({ key: 'userName' })}
                                                        value={user?.userName || ''}
                                                        disabled={process?.showLoading}
                                                        placeholder={labels?.user?.[languageObj?.id]}
                                                        title={labels?.user?.[languageObj?.id]} />
                                                </div>
                                                <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.userName)}</div>
                                            </div>

                                            {/* Code */}
                                            <div className='form-group mb-4'>
                                                <label className='small font-weight-bold text-uppercase form-label-uppercase'>
                                                    {labels?.code?.[languageObj?.id]}
                                                </label>
                                                <div className='position-relative'>
                                                    <i className={`${icons?.fasFa?.idCard || 'fas fa-id-card'} position-absolute input-icon-left`} />
                                                    <input className='form-control border-0 shadow-sm form-input-icon'
                                                        onChange={(e) => setFormValue({ key: 'code', value: e.target.value })}
                                                        onFocus={() => handleInputFocus({ key: 'code' })}
                                                        value={user?.code || ''}
                                                        disabled={process?.showLoading}
                                                        placeholder={labels?.code?.[languageObj?.id]}
                                                        autoComplete='username'
                                                        title={labels?.code?.[languageObj?.id]} />
                                                </div>
                                                <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.code)}</div>
                                            </div>

                                            {/* Password */}
                                            <div className='form-group mb-4'>
                                                <label className='small font-weight-bold text-uppercase form-label-uppercase'>
                                                    {labels?.password?.[languageObj?.id]}
                                                </label>
                                                <div className='position-relative'>
                                                    <i className={`${icons?.fasFa?.key} position-absolute input-icon-left`} />
                                                    <input className='form-control border-0 shadow-sm form-input-icon-both'
                                                        type={showPassword ? 'text' : 'password'}
                                                        onFocus={() => handleInputFocus({ key: 'password' })}
                                                        onChange={(e) => setFormValue({ key: 'password', value: e.target.value })}
                                                        placeholder={labels?.password?.[languageObj?.id]}
                                                        disabled={process?.showLoading}
                                                        value={user?.password || ''}
                                                        autoComplete='current-password'
                                                        title={labels?.password?.[languageObj?.id]} />
                                                    <i className={`${!showPassword ? icons?.fasFa?.eyeSlash : icons?.fasFa?.eye} position-absolute input-icon-right`}
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        style={{ cursor: 'pointer' }} />
                                                </div>
                                                <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.password)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notifications Section */}
                                    <div className='row mt-4'>
                                        <div className='col-12 col-md-6'>
                                            <div className='d-flex align-items-center mb-3'>
                                                <span className='small font-weight-bold text-uppercase text-muted mr-2'>{labels?.notifications?.[languageObj?.id]}</span>
                                                <div className='flex-grow-1 border-top' style={{ borderColor: 'var(--border-default)' }} />
                                            </div>

                                            {/* Email Notifications */}
                                            <div className='form-group mb-4'>
                                                <label className='small font-weight-bold text-uppercase form-label-uppercase'>
                                                    {labels?.email?.[languageObj?.id]}
                                                </label>
                                                <div className='position-relative'>
                                                    <i className={`${icons?.fasFa?.envelope} position-absolute input-icon-left`} />
                                                    <input className='form-control border-0 shadow-sm form-input-icon'
                                                        onChange={(e) => setFormValue({ key: 'notificationsEmail', value: e.target.value })}
                                                        onFocus={() => handleInputFocus({ key: 'notificationsEmail' })}
                                                        value={user?.notifications?.email || ''}
                                                        disabled={process?.showLoading}
                                                        placeholder={labels?.emailPlaceholder?.[languageObj?.id]}
                                                        title={labels?.email?.[languageObj?.id]} />
                                                </div>
                                                <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.notificationsEmail)}</div>
                                            </div>

                                            {/* Telegram Notifications */}
                                            <div className='form-group mb-4'>
                                                <label className='small font-weight-bold text-uppercase form-label-uppercase'>
                                                    {labels?.telegram?.[languageObj?.id]}
                                                </label>
                                                <div className='position-relative'>
                                                    <i className={`${icons?.fasFa?.paperPlane} position-absolute input-icon-left`} />
                                                    <input className='form-control border-0 shadow-sm form-input-icon'
                                                        onChange={(e) => setFormValue({ key: 'notificationsTelegram', value: e.target.value })}
                                                        onFocus={() => handleInputFocus({ key: 'notificationsTelegram' })}
                                                        onKeyDown={(e) => controlMethods.inputs.minusFirtsMaxLengthOnLyNumbers({ e: e, currenTarget: e.currentTarget, maxLength: userNotifications.telegram.maxLength, subMethod: 'inputUserNotificationsTelegram' })}
                                                        value={user?.notifications?.telegram || ''}
                                                        disabled={process?.showLoading}
                                                        placeholder={labels?.telegram?.[languageObj?.id]}
                                                        title={labels?.telegram?.[languageObj?.id]} />
                                                </div>
                                                <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.notificationsTelegram)}</div>
                                            </div>
                                        </div>
                                        <div className='col-12 col-md-6'></div>
                                    </div>
                                </div>

                                <div className='card-footer border-0 bg-white px-4 pb-4'>
                                    <div className='d-flex flex-column flex-md-row justify-content-md-end align-items-stretch align-items-md-center' style={{ gap: '8px' }}>
                                        {/* Save Button */}
                                        {/* <button className='btn btn-primary shadow-sm font-weight-bold px-4' */}
                                        <button className='btn btn-primary shadow-sm font-weight-bold px-4 btn-primary'
                                            disabled={process?.showLoading}
                                            data-title={labels?.save?.[languageObj?.id]}
                                            type='submit'>
                                            {!process?.save ? (
                                                <i className={`${icons?.fasFa?.save} mr-1`} />
                                            ) : (
                                                <span className={react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                                            )} {labels?.save?.[languageObj?.id]}
                                        </button>

                                        {/* Authorizate Button */}
                                        <button className='btn btn-warning shadow-sm font-weight-bold px-4 text-dark btn-general'
                                            disabled={process?.showLoading}
                                            data-title={labels?.authorizate?.[languageObj?.id]}
                                            type='button'
                                            onClick={(e) => getUserAuthorize({ e: e, subMethod: 'btnGetUserAuthorization' })}>
                                            {!process?.authorizate ? (
                                                <i className={`${icons?.fasFa?.idCard} mr-1`} />
                                            ) : (
                                                <span className={react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                                            )} {labels?.authorizate?.[languageObj?.id]}
                                        </button>

                                        {/* Get Token Button */}
                                        <button className='btn btn-secondary shadow-sm font-weight-bold px-4 text-white btn-general'
                                            disabled={process?.showLoading}
                                            data-title={labels?.getToken?.[languageObj?.id]}
                                            type='button'
                                            onClick={(e) => getUserToken({ e: e, subMethod: 'btnGetUserToken' })}>
                                            {!process?.getToken ? (
                                                <i className={`${icons?.fasFa?.key} mr-1`} />
                                            ) : (
                                                <span className={react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                                            )} {labels?.getToken?.[languageObj?.id]}
                                        </button>

                                        {/* Clear Token Logs Button */}
                                        <button className='btn btn-danger shadow-sm font-weight-bold px-4 text-white btn-general'
                                            disabled={process?.showLoading}
                                            data-title={labels?.clearTokenLogs?.[languageObj?.id]}
                                            type='button'
                                            onClick={(e) => clearUserTokenLogs({ e: e, subMethod: 'btnClearTokenLogs' })}>
                                            {!process?.clearTokenLogs ? (
                                                <i className={`${icons?.fasFa?.eraser} mr-1`} />
                                            ) : (
                                                <span className={react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                                            )} {labels?.clearTokenLogs?.[languageObj?.id]}
                                        </button>

                                        {/* Get User ML Info Button */}
                                        <button className='btn btn-info shadow-sm font-weight-bold px-4 text-white btn-general'
                                            disabled={process?.showLoading}
                                            data-title={labels?.getUserInfo?.[languageObj?.id]}
                                            type='button'
                                            onClick={(e) => getUserInfo({ e: e, subMethod: 'btnGetUserInfo' })}>
                                            {!process?.getUserInfo ? (
                                                <i className={`${icons?.fasFa?.addressCard} mr-1`} />
                                            ) : (
                                                <span className={react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                                            )} {labels?.getUserInfo?.[languageObj?.id]}
                                        </button>

                                        {/* Back Link */}
                                        {/* <NavLink className='btn btn-light border shadow-sm font-weight-bold px-4 text-secondary btn-general'
                                            to='/accounts'
                                            >
                                            <i className={`${icons?.fasFa?.arrowAltCircleLeft} mr-1`} /> {labels?.back?.[languageObj?.id]}
                                        </NavLink> */}
                                        <button className='btn btn-light border shadow-sm font-weight-bold px-4 text-secondary btn-general'
                                            disabled={process?.showLoading}
                                            data-title={labels?.back?.[languageObj?.id]}
                                            type='button'
                                            onClick={(e) => gotoBack({ e: e, subMethod: 'btnGetUserInfo' })}>
                                            {!process?.back ? (
                                                <i className={`${icons?.fasFa?.arrowAltCircleLeft} mr-1`} />
                                            ) : (
                                                <span className={react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                                            )} {labels?.back?.[languageObj?.id]}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

const styles_ = {
    pictureUser: {
        width: '85px',
        maxWidth: '100%',
        height: 'auto'
    }
};